import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { api, describeError, useOp } from '../../api.ts';
import { Link, useRouter } from '../../router.tsx';
import { runWrite, useChanges } from '../../ui/changes.ts';
import { step, useKeys } from '../../ui/keys.ts';
import { Empty, ErrorBox, Kbd, Loading } from '../../ui/kit.tsx';
import { useToast } from '../../ui/toasts.tsx';
import './sponsored.css';
import type { CampaignStatus, DiscountType, SponsoredCampaign, SponsoredOptions, SponsoredReport, VideoInfo } from './types.ts';

const STATUS_TONE: Record<CampaignStatus, string> = { active: 'badge-ok', draft: 'badge-muted', paused: 'badge-warn', ended: 'badge-muted' };

function discountText(c: Pick<SponsoredCampaign, 'discountType' | 'discountValue' | 'discountCurrency' | 'offerLabel'>): string {
  if (c.discountType === 'percent') return `${c.discountValue ?? 0}%`;
  if (c.discountType === 'amount') return new Intl.NumberFormat('en-GB', { style: 'currency', currency: c.discountCurrency ?? 'GBP', maximumFractionDigits: 0 }).format(c.discountValue ?? 0);
  return c.offerLabel ?? 'Offer';
}

/** All sponsored exclusives on the sites the user can see. */
export function SponsoredList() {
  const changes = useChanges();
  const { navigate } = useRouter();
  const list = useOp<{ rows: SponsoredCampaign[] }>('admin.sponsored.list', {}, changes);
  const [cursor, setCursor] = useState(0);
  const rows = list.data?.rows ?? [];
  useKeys({
    j: () => setCursor((c) => step(c, 1, rows.length)),
    k: () => setCursor((c) => step(c, -1, rows.length)),
    enter: () => rows[cursor] && navigate(`/sponsored/${rows[cursor]!.ref}`),
    o: () => rows[cursor] && navigate(`/sponsored/${rows[cursor]!.ref}`),
    n: () => navigate('/sponsored/new'),
  });
  return (
    <section className="page">
      <header className="page-head">
        <div>
          <h1>Sponsored exclusives</h1>
          <p className="muted">
            A brand pays for a better, personal code that shoppers unlock by watching the brand's own short video. Always labelled as an ad, started by the
            shopper, and the best public code stays one tap away. Only offered when it is better than the public code.
          </p>
        </div>
        <Link className="btn btn-primary" to="/sponsored/new">
          New campaign <Kbd>n</Kbd>
        </Link>
      </header>
      {list.error ? <ErrorBox error={list.error} onRetry={list.reload} /> : null}
      {!list.data && !list.error ? <Loading /> : null}
      {list.data && rows.length === 0 ? <Empty>No sponsored exclusives yet.</Empty> : null}
      {rows.length ? (
        <table className="table" aria-label="Sponsored exclusives">
          <thead>
            <tr>
              <th scope="col">Brand</th>
              <th scope="col">Site</th>
              <th scope="col">Offer</th>
              <th scope="col">Status</th>
              <th scope="col" className="num">
                Codes left
              </th>
              <th scope="col">Price</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.ref} className={`clickable ${index === cursor ? 'cursor' : ''}`} onClick={() => navigate(`/sponsored/${row.ref}`)} onMouseEnter={() => setCursor(index)}>
                <td>
                  <strong>{row.brandLabel}</strong>
                  <div className="small muted">{row.merchantName}</div>
                </td>
                <td>{row.siteName}</td>
                <td>
                  {discountText(row)} · {row.offerTitle}
                  {row.isDemo ? <span className="badge badge-warn sx-gap">Demo</span> : null}
                </td>
                <td>
                  <span className={`badge ${STATUS_TONE[row.status]}`}>{row.status}</span>
                </td>
                <td className="num">
                  {row.codesAvailable}
                  {row.lowStock ? <span className="badge badge-warn sx-gap">Low</span> : null}
                </td>
                <td className="small">
                  {row.pricePerCompletedView.toFixed(2)} / view · {row.pricePerCodeIssued.toFixed(2)} / member code ({row.priceCurrency})
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </section>
  );
}

interface Draft {
  siteRef: string;
  merchantRef: string;
  brandLabel: string;
  status: CampaignStatus;
  videoUrl: string;
  videoTitle: string;
  videoChannel: string;
  videoStartSeconds: number;
  watchSeconds: number;
  minWatchPercent: number;
  offerTitle: string;
  discountType: DiscountType;
  discountValue: string;
  discountCurrency: string;
  offerLabel: string;
  conditions: string;
  offerEndsAt: string;
  fallbackCouponRef: string;
  startsAt: string;
  endsAt: string;
  pricePerCompletedView: string;
  pricePerCodeIssued: string;
  priceCurrency: string;
  memberPathAllowed: boolean;
  provenCustomerPathAllowed: boolean;
  provenMinPurchases: number;
  provenWindowDays: number;
  provenUnlockOnPending: boolean;
  provenExpectedValidationDays: number;
  lowStockThreshold: number;
}

const EMPTY: Draft = {
  siteRef: '',
  merchantRef: '',
  brandLabel: '',
  status: 'draft',
  videoUrl: '',
  videoTitle: '',
  videoChannel: '',
  videoStartSeconds: 0,
  watchSeconds: 15,
  minWatchPercent: 90,
  offerTitle: '',
  discountType: 'percent',
  discountValue: '',
  discountCurrency: 'GBP',
  offerLabel: '',
  conditions: '',
  offerEndsAt: '',
  fallbackCouponRef: '',
  startsAt: '',
  endsAt: '',
  pricePerCompletedView: '0.05',
  pricePerCodeIssued: '0.50',
  priceCurrency: 'GBP',
  memberPathAllowed: false,
  provenCustomerPathAllowed: false,
  provenMinPurchases: 1,
  provenWindowDays: 365,
  provenUnlockOnPending: false,
  provenExpectedValidationDays: 45,
  lowStockThreshold: 20,
};

const day = (iso: string | null) => (iso ? iso.slice(0, 10) : '');
const toIso = (value: string, endOfDay = false) => (value ? new Date(`${value}T${endOfDay ? '23:59:59' : '00:00:00'}Z`).toISOString() : null);

function fromCampaign(c: SponsoredCampaign): Draft {
  return {
    siteRef: c.siteRef,
    merchantRef: c.merchantRef,
    brandLabel: c.brandLabel,
    status: c.status,
    videoUrl: c.videoUrl,
    videoTitle: c.videoTitle ?? '',
    videoChannel: c.videoChannel ?? '',
    videoStartSeconds: c.videoStartSeconds,
    watchSeconds: c.watchSeconds,
    minWatchPercent: Math.round(c.minWatchRatio * 100),
    offerTitle: c.offerTitle,
    discountType: c.discountType,
    discountValue: c.discountValue === null ? '' : String(c.discountValue),
    discountCurrency: c.discountCurrency ?? 'GBP',
    offerLabel: c.offerLabel ?? '',
    conditions: c.conditions.join('\n'),
    offerEndsAt: day(c.offerEndsAt),
    fallbackCouponRef: c.fallbackCouponRef ?? '',
    startsAt: day(c.startsAt),
    endsAt: day(c.endsAt),
    pricePerCompletedView: String(c.pricePerCompletedView),
    pricePerCodeIssued: String(c.pricePerCodeIssued),
    priceCurrency: c.priceCurrency,
    memberPathAllowed: c.memberPathAllowed,
    provenCustomerPathAllowed: c.provenCustomerPathAllowed,
    provenMinPurchases: c.provenMinPurchases,
    provenWindowDays: c.provenWindowDays,
    provenUnlockOnPending: c.provenUnlockOnPending,
    provenExpectedValidationDays: c.provenExpectedValidationDays,
    lowStockThreshold: c.lowStockThreshold,
  };
}

function toInput(d: Draft, ref: string | null) {
  return {
    ...(ref ? { ref } : {}),
    siteRef: d.siteRef,
    merchantRef: d.merchantRef,
    brandLabel: d.brandLabel,
    status: d.status,
    videoUrl: d.videoUrl,
    videoTitle: d.videoTitle || null,
    videoChannel: d.videoChannel || null,
    videoStartSeconds: d.videoStartSeconds,
    watchSeconds: d.watchSeconds,
    minWatchRatio: d.minWatchPercent / 100,
    offerTitle: d.offerTitle,
    discountType: d.discountType,
    discountValue: d.discountType === 'other' ? null : Number(d.discountValue) || null,
    discountCurrency: d.discountType === 'amount' ? d.discountCurrency : null,
    offerLabel: d.offerLabel || null,
    conditions: d.conditions.split('\n').map((line) => line.trim()).filter(Boolean),
    offerEndsAt: toIso(d.offerEndsAt, true),
    fallbackCouponRef: d.fallbackCouponRef || null,
    startsAt: toIso(d.startsAt),
    endsAt: toIso(d.endsAt, true),
    pricePerCompletedView: Number(d.pricePerCompletedView) || 0,
    pricePerCodeIssued: Number(d.pricePerCodeIssued) || 0,
    priceCurrency: d.priceCurrency,
    memberPathAllowed: d.memberPathAllowed,
    provenCustomerPathAllowed: d.provenCustomerPathAllowed,
    provenMinPurchases: d.provenMinPurchases,
    provenWindowDays: d.provenWindowDays,
    provenUnlockOnPending: d.provenUnlockOnPending,
    provenExpectedValidationDays: d.provenExpectedValidationDays,
    lowStockThreshold: d.lowStockThreshold,
  };
}

/** Create or edit one campaign: settings, live card preview, code pool and the brand report. */
export function SponsoredEditor({ campaignRef }: { campaignRef: string | null }) {
  const toast = useToast();
  const changes = useChanges();
  const { navigate } = useRouter();
  const existing = useOp<SponsoredCampaign>('admin.sponsored.get', { ref: campaignRef }, changes, campaignRef !== null);
  const options = useOp<SponsoredOptions>('admin.sponsored.options', {}, 0);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [loadedRef, setLoadedRef] = useState<string | null>(null);
  const [video, setVideo] = useState<VideoInfo | null>(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (existing.data && existing.data.ref !== loadedRef) {
      setDraft(fromCampaign(existing.data));
      setLoadedRef(existing.data.ref);
    }
  }, [existing.data, loadedRef]);

  const canEdit = campaignRef === null ? (options.data?.sites.length ?? 0) > 0 : (existing.data?.canEdit ?? false);
  const site = options.data?.sites.find((s) => s.ref === draft.siteRef);
  const merchant = site?.merchants.find((m) => m.ref === draft.merchantRef);
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => setDraft((d) => ({ ...d, [key]: value }));

  const checkVideo = async () => {
    if (!draft.videoUrl.trim()) return;
    setChecking(true);
    try {
      const info = await api.op<VideoInfo>('admin.sponsored.videoInfo', { url: draft.videoUrl.trim() });
      setVideo(info);
      if (info.title || info.channel) setDraft((d) => ({ ...d, videoTitle: info.title ?? d.videoTitle, videoChannel: info.channel ?? d.videoChannel }));
    } catch (error) {
      toast('error', describeError(error));
    } finally {
      setChecking(false);
    }
  };

  const save = async (event: FormEvent) => {
    event.preventDefault();
    const saved = await runWrite<SponsoredCampaign>(toast, 'admin.sponsored.save', toInput(draft, campaignRef), (c) => `Saved ${c.brandLabel} on ${c.siteName}`);
    if (saved && campaignRef === null) navigate(`/sponsored/${saved.ref}`, { replace: true });
  };

  if (campaignRef !== null && existing.error) return <ErrorBox error={existing.error} onRetry={existing.reload} />;
  if ((campaignRef !== null && !existing.data) || !options.data) return <Loading />;

  return (
    <section className="page sx-editor">
      <header className="page-head">
        <div>
          <p className="small">
            <Link to="/sponsored">Sponsored exclusives</Link>
          </p>
          <h1>{campaignRef ? `${draft.brandLabel || 'Campaign'} on ${existing.data?.siteName ?? ''}` : 'New sponsored exclusive'}</h1>
          {existing.data?.isDemo ? <p className="text-warn">Demo campaign: fake codes, no agreement with the brand. Production needs the brand's written agreement.</p> : null}
          {!canEdit ? <p className="muted">Read only: owners and admins of the site edit campaigns.</p> : null}
        </div>
      </header>

      <div className="sx-layout">
        <form className="card" onSubmit={save} aria-label="Campaign">
          <fieldset disabled={!canEdit} className="sx-fieldset">
            <h2 className="section-title">Brand and site</h2>
            <div className="form-grid">
              <label>
                Site
                <select required value={draft.siteRef} disabled={campaignRef !== null} onChange={(e) => setDraft((d) => ({ ...d, siteRef: e.target.value, merchantRef: '', fallbackCouponRef: '' }))}>
                  <option value="">Choose…</option>
                  {options.data.sites.map((s) => (
                    <option key={s.ref} value={s.ref}>
                      {s.name}
                    </option>
                  ))}
                  {campaignRef && existing.data && !site ? <option value={existing.data.siteRef}>{existing.data.siteName}</option> : null}
                </select>
              </label>
              <label>
                Merchant
                <select required value={draft.merchantRef} onChange={(e) => setDraft((d) => ({ ...d, merchantRef: e.target.value, fallbackCouponRef: '' }))}>
                  <option value="">Choose…</option>
                  {site?.merchants.map((m) => (
                    <option key={m.ref} value={m.ref}>
                      {m.name}
                    </option>
                  ))}
                  {campaignRef && existing.data && !merchant ? <option value={existing.data.merchantRef}>{existing.data.merchantName}</option> : null}
                </select>
              </label>
              <label>
                Shown as "Sponsored by"
                <input required maxLength={80} value={draft.brandLabel} onChange={(e) => set('brandLabel', e.target.value)} />
              </label>
              <label>
                Status
                <select value={draft.status} onChange={(e) => set('status', e.target.value as CampaignStatus)}>
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                  <option value="ended">Ended</option>
                </select>
              </label>
            </div>

            <h2 className="section-title">The brand's own video</h2>
            <div className="form-grid">
              <label className="sx-wide">
                YouTube link from the brand's channel, or our hosted MP4 (preferred)
                <span className="inline-field">
                  <input required value={draft.videoUrl} onChange={(e) => set('videoUrl', e.target.value)} onBlur={() => void checkVideo()} placeholder="https://www.youtube.com/watch?v=…" />
                  <button type="button" className="btn btn-small" onClick={() => void checkVideo()} disabled={checking}>
                    {checking ? 'Checking…' : 'Check'}
                  </button>
                </span>
              </label>
              {video ? (
                <p className={`sx-wide small ${video.embeddable ? 'muted' : 'text-warn'}`} role="status">
                  {video.kind === 'hosted'
                    ? 'Hosted MP4: no third-party embed.'
                    : video.embeddable
                      ? `Published by ${video.channel}. Make sure this is the brand's own channel. Duration is not available from YouTube; set the watch time below.`
                      : 'YouTube does not allow embedding this video, or it is private. Ask the brand for an embeddable upload or an MP4.'}
                </p>
              ) : null}
              <label>
                Video title
                <input value={draft.videoTitle} onChange={(e) => set('videoTitle', e.target.value)} />
              </label>
              <label>
                Channel
                <input value={draft.videoChannel} onChange={(e) => set('videoChannel', e.target.value)} />
              </label>
              <label>
                Start at (seconds)
                <input type="number" min={0} max={3600} value={draft.videoStartSeconds} onChange={(e) => set('videoStartSeconds', Number(e.target.value))} />
              </label>
              <label>
                Watch time (seconds)
                <input type="number" min={5} max={60} value={draft.watchSeconds} onChange={(e) => set('watchSeconds', Number(e.target.value))} />
              </label>
              <label>
                Must really play (%)
                <input type="number" min={50} max={100} value={draft.minWatchPercent} onChange={(e) => set('minWatchPercent', Number(e.target.value))} />
              </label>
            </div>

            <h2 className="section-title">Offer</h2>
            <div className="form-grid">
              <label className="sx-wide">
                Offer title
                <input required minLength={3} maxLength={160} value={draft.offerTitle} onChange={(e) => set('offerTitle', e.target.value)} placeholder="25% off your next order" />
              </label>
              <label>
                Discount
                <select value={draft.discountType} onChange={(e) => set('discountType', e.target.value as DiscountType)}>
                  <option value="percent">Percent</option>
                  <option value="amount">Amount</option>
                  <option value="other">Other</option>
                </select>
              </label>
              {draft.discountType === 'other' ? (
                <label>
                  Short label
                  <input required maxLength={60} value={draft.offerLabel} onChange={(e) => set('offerLabel', e.target.value)} placeholder="Free gift" />
                </label>
              ) : (
                <label>
                  Value
                  <input required type="number" min={0.01} step="0.01" value={draft.discountValue} onChange={(e) => set('discountValue', e.target.value)} />
                </label>
              )}
              {draft.discountType === 'amount' ? (
                <label>
                  Currency
                  <input required maxLength={3} value={draft.discountCurrency} onChange={(e) => set('discountCurrency', e.target.value.toUpperCase())} />
                </label>
              ) : null}
              <label>
                Public code next to it
                <select value={draft.fallbackCouponRef} onChange={(e) => set('fallbackCouponRef', e.target.value)}>
                  <option value="">The best live public code (automatic)</option>
                  {merchant?.codes.map((code) => (
                    <option key={code.ref} value={code.ref}>
                      {code.title}
                    </option>
                  ))}
                </select>
              </label>
              <label className="sx-wide">
                Conditions shown before watching (one per line)
                <textarea rows={3} value={draft.conditions} onChange={(e) => set('conditions', e.target.value)} />
              </label>
              <label>
                Offer ends
                <input type="date" value={draft.offerEndsAt} onChange={(e) => set('offerEndsAt', e.target.value)} />
              </label>
              <label>
                Campaign starts
                <input type="date" value={draft.startsAt} onChange={(e) => set('startsAt', e.target.value)} />
              </label>
              <label>
                Campaign ends
                <input type="date" value={draft.endsAt} onChange={(e) => set('endsAt', e.target.value)} />
              </label>
            </div>
            <p className="small muted">The video is only offered when this offer is better than every live public code of the merchant on the site.</p>

            <h2 className="section-title">Price for the brand</h2>
            <div className="form-grid">
              <label>
                Per completed view
                <input type="number" min={0} step="0.01" value={draft.pricePerCompletedView} onChange={(e) => set('pricePerCompletedView', e.target.value)} />
              </label>
              <label>
                Per code to members
                <input type="number" min={0} step="0.01" value={draft.pricePerCodeIssued} onChange={(e) => set('pricePerCodeIssued', e.target.value)} />
              </label>
              <label>
                Currency
                <input maxLength={3} value={draft.priceCurrency} onChange={(e) => set('priceCurrency', e.target.value.toUpperCase())} />
              </label>
              <label>
                Low stock warning at
                <input type="number" min={0} value={draft.lowStockThreshold} onChange={(e) => set('lowStockThreshold', Number(e.target.value))} />
              </label>
            </div>

            <h2 className="section-title">Members and proven customers</h2>
            <p className="small muted">Stored now; shown on the sites once member accounts launch (feature flag SPONSORED_MEMBER_PATH). Proven means purchases of this brand through our links.</p>
            <div className="form-grid">
              <label className="inline-check">
                <input type="checkbox" checked={draft.memberPathAllowed} onChange={(e) => set('memberPathAllowed', e.target.checked)} /> Members get the code without the video
              </label>
              <label className="inline-check">
                <input type="checkbox" checked={draft.provenCustomerPathAllowed} onChange={(e) => set('provenCustomerPathAllowed', e.target.checked)} /> Proven customers of this brand get it without the video
              </label>
              {draft.provenCustomerPathAllowed ? (
                <>
                  <label>
                    Purchases needed
                    <input type="number" min={1} max={20} value={draft.provenMinPurchases} onChange={(e) => set('provenMinPurchases', Number(e.target.value))} />
                  </label>
                  <label>
                    Within (days)
                    <input type="number" min={7} max={1095} value={draft.provenWindowDays} onChange={(e) => set('provenWindowDays', Number(e.target.value))} />
                  </label>
                  <label className="inline-check">
                    <input type="checkbox" checked={draft.provenUnlockOnPending} onChange={(e) => set('provenUnlockOnPending', e.target.checked)} /> Unlock already while the sale is pending (revoked if declined)
                  </label>
                  <label>
                    Typical confirmation (days)
                    <input type="number" min={1} max={180} value={draft.provenExpectedValidationDays} onChange={(e) => set('provenExpectedValidationDays', Number(e.target.value))} />
                  </label>
                </>
              ) : null}
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                Save
              </button>
            </div>
          </fieldset>
        </form>

        <aside className="sx-side">
          <CardPreview draft={draft} merchantName={merchant?.name ?? existing.data?.merchantName ?? 'the store'} publicCode={merchant?.codes.find((c) => c.ref === draft.fallbackCouponRef)?.title ?? null} />
          {campaignRef && existing.data ? <CodePool campaign={existing.data} canEdit={canEdit} /> : null}
          {campaignRef ? <Report campaignRef={campaignRef} /> : null}
        </aside>
      </div>
    </section>
  );
}

function CardPreview({ draft, merchantName, publicCode }: { draft: Draft; merchantName: string; publicCode: string | null }) {
  const figure = discountText({ discountType: draft.discountType, discountValue: Number(draft.discountValue) || 0, discountCurrency: draft.discountCurrency, offerLabel: draft.offerLabel });
  const brand = draft.brandLabel || 'Brand';
  return (
    <div className="card" aria-label="Card preview">
      <h2 className="section-title">Preview on the site</h2>
      <div className="sx-preview">
        <p className="sx-label">
          <span className="sx-ad">Ad</span> Sponsored by {brand}
        </p>
        <p className="sx-title">{draft.offerTitle || 'Offer title'}</p>
        <ul className="small muted">
          <li>One personal code per person, made for you by {brand}.</li>
          {draft.conditions
            .split('\n')
            .filter((line) => line.trim())
            .map((line) => (
              <li key={line}>{line}</li>
            ))}
        </ul>
        <div className="sx-choices">
          <span className="sx-choice sx-watch">
            <strong>{figure}</strong> Sponsored by {brand}: watch {draft.watchSeconds} s, get your personal {figure} code
          </span>
          <span className="sx-choice">
            <strong>{publicCode ?? 'Best public code'}</strong> {merchantName} public code, no video
          </span>
        </div>
        <p className="small muted">In the video: "Skip, give me the public code now" is always visible.</p>
      </div>
    </div>
  );
}

function CodePool({ campaign, canEdit }: { campaign: SponsoredCampaign; canEdit: boolean }) {
  const toast = useToast();
  const [csv, setCsv] = useState('');
  const upload = async (event: FormEvent) => {
    event.preventDefault();
    const result = await runWrite<{ added: number; duplicates: number; invalid: number; available: number; lowStock: boolean }>(
      toast,
      'admin.sponsored.uploadCodes',
      { ref: campaign.ref, csv },
      (r) => `Added ${r.added} codes (${r.duplicates} duplicates, ${r.invalid} invalid skipped). ${r.available} available${r.lowStock ? ' — low stock' : ''}.`,
    );
    if (result) setCsv('');
  };
  const readFile = async (file: File | undefined) => {
    if (file) setCsv(await file.text());
  };
  return (
    <div className="card">
      <h2 className="section-title">Code pool</h2>
      <p>
        <strong>{campaign.codesAvailable}</strong> codes available
        {campaign.lowStock ? <span className="badge badge-warn sx-gap">Low stock: ask {campaign.brandLabel} for more</span> : null}
      </p>
      <p className="small muted">Unique codes supplied by the brand, one per shopper. Uploaded codes are never shown again here or on the sites.</p>
      {canEdit ? (
        <form onSubmit={upload} aria-label="Upload codes">
          <label className="block">
            CSV or one code per line
            <textarea rows={4} value={csv} onChange={(e) => setCsv(e.target.value)} className="mono" />
          </label>
          <div className="form-actions">
            <input type="file" accept=".csv,.txt,text/csv,text/plain" onChange={(e) => void readFile(e.target.files?.[0])} aria-label="Choose a CSV file" />
            <button type="submit" className="btn btn-small btn-primary" disabled={!csv.trim()}>
              Upload
            </button>
          </div>
        </form>
      ) : null}
    </div>
  );
}

function Report({ campaignRef }: { campaignRef: string }) {
  const changes = useChanges();
  const report = useOp<SponsoredReport>('admin.sponsored.report', { ref: campaignRef }, changes);
  const money = useMemo(() => (value: number, currency: string | null) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency ?? 'GBP' }).format(value), []);
  if (report.error) return <ErrorBox error={report.error} onRetry={report.reload} />;
  if (!report.data) return <Loading />;
  const r = report.data;
  const completion = r.sessions.started ? Math.round((r.sessions.completed / r.sessions.started) * 100) : 0;
  return (
    <div className="card">
      <h2 className="section-title">Report for the brand</h2>
      <dl className="facts">
        <dt>Videos started</dt>
        <dd>{r.sessions.started}</dd>
        <dt>Watched to the end</dt>
        <dd>
          {r.sessions.completed} ({completion}%)
        </dd>
        <dt>Abandoned</dt>
        <dd>
          {r.sessions.abandoned}, of which {r.sessions.fallbackTaken} took the public code
        </dd>
        <dt>Codes issued</dt>
        <dd>
          {r.codes.video} after video · {r.codes.member} members · {r.codes.proven} proven customers
        </dd>
        <dt>Failed codes replaced</dt>
        <dd>{r.codes.failedReported}</dd>
        <dt>Clickouts with a code</dt>
        <dd>{r.clickouts}</dd>
        <dt>Orders</dt>
        <dd>
          {r.orders.count}
          {r.orders.count ? ` · ${money(r.orders.saleAmount, r.orders.currency)} sales · ${money(r.orders.commissionApproved, r.orders.currency)} approved, ${money(r.orders.commissionPending, r.orders.currency)} pending commission` : ''}
        </dd>
        <dt>Cost to the brand</dt>
        <dd>
          <strong>{money(r.cost.amount, r.cost.currency)}</strong>
          <span className="small muted">
            {' '}
            ({r.cost.completedViews} views + {r.cost.codesIssuedToMembers} member codes)
          </span>
        </dd>
      </dl>
    </div>
  );
}
